// @flow
import isEqual from 'lodash/isEqual'
import moment from 'moment'
import kebabCase from 'lodash/kebabCase'
import type { Period } from 'types'

// DATES / TIMES

export const DATE = 'YYYY-MM-DD'
export const DATETIME = 'YYYY-MM-DDTHH:mm:ss.SSSSSSZ'
export const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
export const TIME = 'hh:mm:ss'
export const TIME_24HR = 'HH:mm:ss'
export const TIME_AMPM = 'h:mma'

export function days(weekdays: number[], short: boolean = false): string {
  const prefix = short ? '' : 'Every '
  if (weekdays.length === 0) return 'Not Set'
  if (isEqual(weekdays.sort(), [0, 1, 2, 3, 4, 5, 6])) return 'Every Day'
  if (isEqual(weekdays.sort(), [1, 2, 3, 4, 5])) return short ? 'Weekdays' : 'Every Weekday'
  if (weekdays.length === 1) return `${prefix}${DAYS[weekdays[0]]}`
  return `${prefix}${weekdays.map(d => DAYS[d].substr(0, 3)).join(', ')}`
}

export function formatDate(date: ?string, format: string): string {
  return date ? moment(date, DATE).format(format) : ''
}

export function formatDateTime(date: ?string, format: string, timezone?: string): string {
  if (!date) return ''
  return timezone
    ? moment(date, DATETIME).tz(timezone).format(format)
    : moment(date, DATETIME).format(format)
}

export function formatTime(time: ?string, format: string): string {
  return time ? moment(time, TIME).format(format) : ''
}

export function formatTimeRange(
  startTime: string,
  endTime: string,
  separator: string = '–',
): string {
  const begins = formatTime(startTime, TIME_AMPM)
  const ends = formatTime(endTime, TIME_AMPM)
  return `${begins}${separator}${ends}`
}

export function calendarTime(time: string, format: string): string {
  const parsed = moment(time, format)
  if (parsed.isBefore(moment().subtract(10, 'months'))) {
    return parsed.format('M/D/YY')
  }
  if (parsed.isBefore(moment().subtract(20, 'hours'))) {
    return parsed.format('MMM d')
  }
  return parsed.format(TIME_AMPM)
}

// MODELS

// returns a string in the format "h:mma–h:mma"
export function formatPeriod(period: Period, separator: string = '–'): string {
  return formatTimeRange(period.beginsAt, period.endsAt, separator)
}

// returns an age in months given a date
export function formatAge(date: ?string): string {
  if (date) {
    const months = moment().diff(moment(date, DATE), 'months')
    if (months < 0) {
      return `expected ${formatDate(date, 'MMM DD')}`
    }
    return `${months} months`
  }
  return date || ''
}

// STRINGS

export function currency(amt: number, decimals?: number): string {
  const dec = decimals === undefined ? (amt % 100 ? 2 : 0) : decimals // eslint-disable-line
  const sign = amt < 0 ? '-' : ''
  // TODO: Make this less hacky
  const parts = Math.abs(amt * 0.01)
    .toFixed(dec)
    .split('.')
  const dollars = parts[0]
  if (dollars.length > 3) {
    const left = dollars.slice(0, dollars.length - 3)
    const right = dollars.slice(dollars.length - 3, dollars.length)
    parts[0] = `${left},${right}`
  }
  return `${sign}$${parts.join('.')}`
}

export function guessFirstName(name: string): string {
  return name.split(/\s+/)[0]
}

// Expects an integer value between 0 and 9999, which maps to percentages between 0% and 99.99%
export function percentage(value: ?number): string {
  if (!value) return ''
  return (value * 0.01).toFixed(2).replace(/\.?0+$/, '')
}

export function pluralize(num: number, txt: string): string {
  return `${num} ${txt}${num === 1 ? '' : 's'}`
}

export function slugify(id: number, name: string): string {
  return `${id}-${kebabCase(name).slice(0, 50)}`
}

export function slugifyQuery(query: string) {
  if (!query) return ''
  return query.replace(/,[\s?]+/, '-').replace(/\s+/, '+')
}

export function e164Format(phone: string) {
  if (!phone) return ''
  return phone.replace(/[^\d]+/g, '').slice(0, 10)
}

export function truncate(txt: string, maxLength?: number = 50): string {
  if (txt.length <= maxLength) return txt

  return `${txt.substring(0, maxLength)}...`
}
